import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { BookOpen, Save, Trash2, Loader2, ChevronDown } from 'lucide-react';

export default function ZohoReportViewManager({ formId, currentView, onLoadView }) {
  const [views, setViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadViews();
  }, [formId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadViews = async () => {
    try {
      const res = await zohoAPI.getReportViews(formId);
      setViews(res.data.views || []);
    } catch {}
  };

  const handleSave = async () => {
    if (!viewName.trim()) return;
    setSaving(true);
    try {
      await zohoAPI.createReportView(formId, {
        name: viewName.trim(),
        ...currentView,
      });
      toast.success('View saved');
      setViewName('');
      setSaveOpen(false);
      loadViews();
    } catch {
      toast.error('Failed to save view');
    }
    setSaving(false);
  };

  const handleDelete = async (viewId) => {
    try {
      await zohoAPI.deleteReportView(viewId);
      toast.success('View deleted');
      loadViews();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="report-views-btn" className="gap-1">
            <BookOpen className="h-3.5 w-3.5" />
            Views
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {views.length === 0 ? (
            <div className="text-xs text-muted-foreground px-2 py-3 text-center">No saved views</div>
          ) : (
            views.map(v => (
              <DropdownMenuItem key={v.id} className="flex items-center justify-between" data-testid={`load-view-${v.id}`}>
                <span className="truncate flex-1 cursor-pointer" onClick={() => onLoadView(v)}>
                  {v.name}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 shrink-0 ml-2"
                  onClick={(e) => { e.stopPropagation(); handleDelete(v.id); }}
                  data-testid={`delete-view-${v.id}`}
                >
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </DropdownMenuItem>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setSaveOpen(true)} data-testid="save-new-view">
            <Save className="h-4 w-4 mr-2" />
            Save Current View
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle>Save Report View</DialogTitle>
            <DialogDescription>Save the current columns, filters, and sort as a named view.</DialogDescription>
          </DialogHeader>
          <Input
            value={viewName}
            onChange={e => setViewName(e.target.value)}
            placeholder="e.g., Engineering Appraisals Q1"
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            data-testid="view-name-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !viewName.trim()} data-testid="confirm-save-view">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
